import { Divider, Stack, Box, Grid } from "@mui/material";
import { Img } from "../Img";
import { formatImgUrl } from "utils/helpers";
import { forwardRef, useMemo } from "react";
import {
  Link,
  StyledRouterA,
  TextMd,
  XsText,
  XsTextMd,
  XsTextSemiBold,
} from "../Text";
import CountryFlag from "../CountryFlag";
import { useIntl } from "react-intl";
import defaultImage from "Assets/images/default_img.webp";
import Row from "../Row";
import { styled } from "@mui/material/styles";
import { SupplierListingItem } from "types/supplier";
import { PlanChip } from "../Plan";
import ListingChip from "../chips/ListingChip";
import { RatingInfors } from "Components/infos";
import VerificationStatus from "Components/tooltip/VerificationStatus";
import { COLORS } from "constants/layout";
import TimeDiff from "Components/TimeDiff";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useBreakpoints from "hooks/useBreakpoint";
import { PLAN_NAMES, SUPPLIER_PLANS } from "constants/data";
import { isNotFreeAndUnverified } from "helpers/plan";
import { PlanDataType } from "types/common";
import { getPlanConfig } from "utils/helpers/planConfig";
import {
  AttributesDisplay,
  CategoryLinks,
  GalleryDisplay,
  OwnerInformation,
  PriceDisplay,
} from "../supplier";
import EllipsisText from "../EllipsisText";

// Initialize dayjs plugins
dayjs.extend(relativeTime);

interface Props {
  item: SupplierListingItem;
  fullMode?: boolean;
  isPageDetail?: boolean;
  isNotShowButton?: boolean;
}

const ListingItemCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      item,
      fullMode = true,
      isPageDetail = false,
      isNotShowButton = false,
    }: Props,
    ref
  ) => {
    const { isMobile } = useBreakpoints();
    const intl = useIntl();
    const NOW = dayjs();
    const {
      farm_name,
      country,
      country_code,
      farm_path,
      farm_avatar,
      gallery_preview = [],
      plan_name,
      short_description,
      export_categories = [],
      activity_tags = [],
      is_biz_verified,
      is_verified_work_email,
      trust_star,
      count_review,
      farm_id,
      last_updated = "",
      identity,
      farmer_name,
      salutation,
      position,
      user_image,
      count_inquiry = 0,
      last_active,

      // Product specific fields
      farm_item_name,
      farm_item,
      farm_item_image,
      farm_item_id,
      farm_item_path,
      farm_tags,
      moq,
      moq_unit,
      price_min = 0,
      price_max = 0,
      price_status,
      type,
      attributes = [],
    } = item;

    // Get plan configurations
    const hasGalleryImages = gallery_preview && gallery_preview.length > 0;
    const planConfig = getPlanConfig(plan_name, isMobile, hasGalleryImages);

    // Determine layout type flags
    const isPremiumNoImage =
      planConfig.layoutVersion === "premium" && !hasGalleryImages;
    const showFlagBelow =
      isPremiumNoImage || planConfig.layoutVersion === "basic";
    const shouldUseFreeLayout = !hasGalleryImages;
    const usePremiumLayout =
      planConfig.layoutVersion === "premium" && !shouldUseFreeLayout;

    // Special layout for non-premium plans on mobile with images
    const premiumPlans = [
      PLAN_NAMES.advance,
      PLAN_NAMES.starter,
      PLAN_NAMES.pro,
    ];
    const isNonPremiumPlan = !!plan_name && !premiumPlans.includes(plan_name);

    // This special layout ONLY applies on mobile for non-premium plans with images
    const useOtherMobileWithImagesLayout =
      isMobile &&
      hasGalleryImages &&
      isNonPremiumPlan &&
      planConfig.layoutVersion === "basic";

    // Prepare media for display based on plan configuration
    const medias = useMemo(
      () =>
        planConfig.canDisplayMedia
          ? gallery_preview.filter((img) => img)
          : [defaultImage],
      [planConfig.canDisplayMedia, gallery_preview]
    );

    const priceStatusConfigs = useMemo(() => {
      switch (price_status) {
        case "COMPETITIVE":
          return { label: "good_price" };
        default:
          return {};
      }
    }, [price_status]);

    const isValidPlan = isNotFreeAndUnverified({ plan_name } as PlanDataType);
    const isProduct = type === "PRODUCT" || farm_item_id != null;
    const hasPrice = (price_min || 0) > 0 || (price_max || 0) > 0;
    const PAYMENT_PLANS = ["Starter", "Advance"];

    // Common component for verification and flag
    const renderVerificationAndFlag = () => (
      <Box display="flex" alignItems="center" gap={1}>
        <VerificationStatus
          data={{
            is_biz_verified: is_biz_verified,
            is_verified_work_email: is_verified_work_email
          }}
          entity="supplier"
        />
        <CountryFlag
          fd_country={country}
          country_code={country_code}
          component="h4"
        />
      </Box>
    );

    // Common component for company info
    const renderCompanyInfo = () => (
      <Row flexWrap="wrap" gap={0.5} alignItems="flex-start">
        {farm_avatar && (
          <Link to={farm_path}>
            <Img
              src={formatImgUrl(farm_avatar, 3)}
              alt="img"
              className="company-avatar"
              fillContainer
              style={{
                objectFit: "contain",
                height: "45px",
                width: "45px",
                objectPosition: "center",
                borderRadius: 0,
              }}
            />
          </Link>
        )}
        <Stack
          gap={0.5}
          flex={1}
          overflow="hidden"
          width="100%"
          sx={{ marginLeft: "0!important" }}
        >
          <StyledRouterA
            to={farm_path}
            style={{ maxWidth: "100%", display: "block" }}
            className="farm-name-link"
          >
            <TextMd
              className="cutoff-text oneline farm-name"
              component="h2"
              sx={{
                fontSize: "14px!important",
              }}
              color={COLORS.green[600]}
              noWrap
              title={farm_name}
            >
              {farm_name}
            </TextMd>
          </StyledRouterA>

          {showFlagBelow ? (
            <>
              <Row gap={0.5}>
                <RatingInfors
                  data={{
                    trust_star,
                    count_review,
                    name: farm_id,
                  }}
                />
              </Row>
              <Box mt={0.5}>{renderVerificationAndFlag()}</Box>
            </>
          ) : (
            <Row gap={0.5}>
              <RatingInfors
                data={{
                  trust_star,
                  count_review,
                  name: farm_id,
                }}
              />
              {renderVerificationAndFlag()}
            </Row>
          )}
        </Stack>
      </Row>
    );

    // Common component for activity tags
    const renderActivityTags = () =>
      activity_tags.length > 0 && (
        <Box
          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, marginTop: 0.5 }}
        >
          <ListingChip
            tags={activity_tags.map((tag) => ({
              tag_name: tag.tag_name,
              description: tag.description || undefined,
            }))}
          />
        </Box>
      );

    // Common component for product name
    const renderProductName = () => (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ width: "100%", overflow: "hidden" }}
      >
        <Stack
          direction="row"
          alignItems="baseline"
          gap={0.5}
          sx={{ width: "100%", overflow: "hidden" }}
        >
          <XsText
            className="farm-item-name"
            color={COLORS.gray[500]}
            sx={{ fontSize: "14px" }}
          >
            {intl.formatMessage({ id: "product" })}:
          </XsText>
          {farm_item_path ? (
            <StyledRouterA
              to={farm_item_path}
              className="product-name-link"
              style={{
                maxWidth: "100%",
                display: "block",
                overflow: "hidden",
              }}
            >
              <XsTextMd
                color={COLORS.green[600]}
                className="cutoff-text oneline product-name"
                title={farm_item_name || ""}
                sx={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  display: "block",
                  fontSize: "14px",
                }}
              >
                {farm_item_name}
              </XsTextMd>
            </StyledRouterA>
          ) : (
            <XsTextMd
              color={COLORS.green[600]}
              sx={{
                maxWidth: "100%",
                overflow: "hidden",
                display: "block",
                fontSize: "14px",
              }}
            >
              {farm_item_name}
            </XsTextMd>
          )}
        </Stack>
      </Stack>
    );

    // Common component for description and categories
    const renderDescriptionAndCategories = () => (
      <Stack gap={0.5}>
        {!isProduct && short_description && (
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              display: "block",
            }}
          >
            <EllipsisText
              variant="body2"
              sx={{ fontWeight: 500, fontSize: 12 }}
            >
              {short_description}
            </EllipsisText>
          </Box>
        )}

        {/* Always display attributes when they exist */}
        <AttributesDisplay attributes={attributes || []} />

        {export_categories.length > 0 && (
          <CategoryLinks categories={export_categories} />
        )}
      </Stack>
    );

    // Common component for inquiries and reviews counter
    const renderInquiriesAndReviews = () => (
      <Stack
        gap={0.5}
        alignItems="flex-end"
        sx={{
          display: {
            xs: "none",
            lg: "flex",
          },
          minWidth: "160px",
          justifyContent: "flex-end",
        }}
      >
        <Box>
          <XsText color={COLORS.gray[500]} className="time-text">
            {last_updated && (
              <TimeDiff startTime={last_updated} endTime={NOW.format()} />
            )}
          </XsText>
        </Box>
        <Stack direction="row" gap={0.5} alignItems="center">
          {count_inquiry > 0 && (
            <Stack direction="row" gap={0.5}>
              <XsTextSemiBold>{count_inquiry}</XsTextSemiBold>
              <XsText color={COLORS.gray[500]}>
                {intl.formatMessage({ id: "inquiries" })}
              </XsText>
            </Stack>
          )}
          {count_inquiry > 0 && (count_review || 0) > 0 && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          )}
          {(count_review || 0) > 0 && (
            <Stack direction="row" gap={0.5}>
              <XsTextSemiBold>{count_review}</XsTextSemiBold>
              <XsText color={COLORS.gray[500]}>
                {intl.formatMessage({ id: "reviews" })}
              </XsText>
            </Stack>
          )}
        </Stack>
      </Stack>
    );

    // Common component for owner information
    const renderOwnerInformation = () =>
      PAYMENT_PLANS.includes(plan_name) && (
        <Box mt="auto" pt={1}>
          <Divider />
          <OwnerInformation
            data={{
              user_image,
              farmer_name,
              salutation,
              position,
              identity,
              farm_id,
              farm_path,
              farm_item_id,
              farm_item_path,
              last_active,
            }}
            isNotShowButton={isNotShowButton || !isValidPlan}
            isFromSupplier={!isProduct}
          />
        </Box>
      );

    return (
      <StyledListingItemCard ref={ref}>
        {/* Position for the plan chip (top left) */}
        <Box
          sx={{ position: "absolute", top: 12, left: -6 }}
          className="plan-flag-container"
        >
          {!isPremiumNoImage &&
            hasGalleryImages &&
            SUPPLIER_PLANS.find((plan) => plan.label === plan_name)?.label2 && (
              <Box className={`plan-flag plan-${plan_name.toLowerCase()}`}>
                <PlanChip plan={plan_name} height={25} variant="glossy" />
              </Box>
            )}
        </Box>

        {/* Main Content */}
        <Grid container spacing={0} columnSpacing={0} sx={{ padding: 0 }}>
          {/* Left - Main Image - Shown for premium plans with images on desktop */}
          {planConfig.showImages &&
            !shouldUseFreeLayout &&
            !useOtherMobileWithImagesLayout && (
              <Grid
                item
                xs={planConfig.gridRatio.image}
                sx={{ padding: 0, marginLeft: 0 }}
              >
                <GalleryDisplay
                  medias={medias}
                  destinationPath={farm_path}
                  planName={plan_name}
                  isProduct={isProduct}
                  productPath={
                    isProduct ? farm_item_path || undefined : undefined
                  }
                />
              </Grid>
            )}

          {/* RENDERING CONDITIONS PRIORITY:
              1. Special mobile layout for non-premium plans with images
              2. Premium layout with gallery 
              3. Basic 2-column layout for free/without images
           */}
          {useOtherMobileWithImagesLayout ? (
            /* Special layout for non-premium plans on mobile with images */
            <Grid item xs={12} sx={{ padding: "8px 12px" }}>
              <Stack gap={1.5}>
                {/* Top Row - Company Info */}
                <Box>
                  {renderCompanyInfo()}
                  {renderActivityTags()}
                </Box>

                {/* Bottom Row - Two columns (Images left, Content right) */}
                <Grid container spacing={1}>
                  {/* Left Column - Images */}
                  <Grid item xs={5} sx={{ padding: 0 }}>
                    <GalleryDisplay
                      medias={medias}
                      destinationPath={farm_path}
                      planName={plan_name}
                      isProduct={isProduct}
                      fullRadius
                      productPath={
                        isProduct ? farm_item_path || undefined : undefined
                      }
                    />
                  </Grid>

                  {/* Right Column - Content */}
                  <Grid item xs={7} sx={{ padding: "8px 12px" }}>
                    <Stack
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Product info if product */}
                      {isProduct && (
                        <Stack>
                          <Stack>{renderProductName()}</Stack>

                          {/* Price & Min Order */}
                          <PriceDisplay
                            hasPrice={hasPrice}
                            price_min={price_min}
                            price_max={price_max}
                            moq={moq}
                            moq_unit={moq_unit}
                            priceStatusConfig={priceStatusConfigs}
                            farm_id={farm_id}
                            farm_path={farm_path}
                            isMobile={isMobile}
                          />
                        </Stack>
                      )}

                      {/* Description & Categories */}
                      {renderDescriptionAndCategories()}

                      {/* Owner Info & Buttons */}
                      {renderOwnerInformation()}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          ) : usePremiumLayout ? (
            /* Premium layout with gallery */
            <Grid
              item
              xs={12}
              md={planConfig.gridRatio.content}
              sx={{ padding: "8px 12px" }}
            >
              <Stack
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {/* Upper Content */}
                <Stack>
                  {/* Company Info */}
                  <Stack gap={0.5}>
                    {/* Company Info Row with Left and Right Blocks */}
                    <Row
                      gap={2}
                      justifyContent="space-between"
                      alignItems="flex-start"
                      flexWrap="wrap"
                    >
                      {/* Left Block - Company Avatar and Name */}
                      <Row flex={1} sx={{ minWidth: { xs: "100%", lg: 0 } }}>
                        {renderCompanyInfo()}
                      </Row>

                      {/* Right Block - Additional Info */}
                      {renderInquiriesAndReviews()}
                    </Row>

                    {/* Tags */}
                    {renderActivityTags()}

                    {/* Product Name & Price (if product) */}
                    {isProduct && (
                      <Stack spacing={0.5} mt={0.5}>
                        {renderProductName()}

                        {/* Price & Min Order */}
                        <PriceDisplay
                          hasPrice={hasPrice}
                          price_min={price_min}
                          price_max={price_max}
                          moq={moq}
                          moq_unit={moq_unit}
                          priceStatusConfig={priceStatusConfigs}
                          farm_id={farm_id}
                          farm_path={farm_path}
                          isMobile={isMobile}
                        />
                      </Stack>
                    )}
                  </Stack>

                  {/* Description & Categories */}
                  <Stack gap={0.5} my={0.5}>
                    {renderDescriptionAndCategories()}
                  </Stack>
                </Stack>

                {/* Owner Info & Buttons - Always at bottom */}
                {renderOwnerInformation()}
              </Stack>
            </Grid>
          ) : (
            /* Alternative 2-column layout for basic/free/premium without images */
            <>
              {/* Left Column - Company Info and Tags */}
              <Grid item xs={12} md={3} sx={{ padding: "8px 12px" }}>
                <Stack gap={1.5}>
                  {/* Company Avatar and Name */}
                  {renderCompanyInfo()}

                  {/* Tags */}
                  {renderActivityTags()}
                </Stack>
              </Grid>

              {/* Right Column - Content */}
              <Grid item xs={12} md={9} sx={{ padding: "8px 12px" }}>
                <Stack
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Upper Content */}
                  <Stack>
                    {/* Product info if product */}
                    {isProduct && (
                      <Stack>
                        <Stack mt={0.5}>{renderProductName()}</Stack>

                        {/* Price & Min Order */}
                        <PriceDisplay
                          hasPrice={hasPrice}
                          price_min={price_min}
                          price_max={price_max}
                          moq={moq}
                          moq_unit={moq_unit}
                          priceStatusConfig={priceStatusConfigs}
                          farm_id={farm_id}
                          farm_path={farm_path}
                          isMobile={isMobile}
                        />
                      </Stack>
                    )}

                    {/* Description & Categories */}
                    {renderDescriptionAndCategories()}
                  </Stack>

                  {/* Owner Info & Buttons - Always at bottom */}
                  {renderOwnerInformation()}
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </StyledListingItemCard>
    );
  }
);

const StyledListingItemCard = styled(Stack)(({ theme }) => ({
  height: "100%",
  position: "relative",
  borderRadius: 8,
  backgroundColor: "#fff",
  border: `1px solid var(--gray-200)`,

  ".plan-flag-container": {
    height: "fit-content",
    zIndex: 2,
    ".plan-flag": {
      position: "relative",
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
      padding: "2px 8px 2px 4px",
      display: "inline-flex",
      alignItems: "center",
      transform: "translateX(-5px)",
    },
  },

  ".main-image": {
    width: "100%",
    height: "100%",
    borderRadius: theme.shape.borderRadius,
    objectFit: "cover",
    maxHeight: "350px",
  },

  ".tag-chip": {
    backgroundColor: COLORS.gray[100],
    color: COLORS.gray[700],
  },

  "& .categories": {
    color: "var(--gray)",
    span: {
      color: "var(--gray-900)",
    },
  },

  ".categories-text a": {
    color: "var(--green-600)",
  },

  ".cutoff-text": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    "&.oneline": {
      whiteSpace: "nowrap",
    },
    "&.twoline": {
      display: "-webkit-box",
      "-webkit-line-clamp": 2,
      "-webkit-box-orient": "vertical",
    },
  },

  ".ellipsis-text": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  ".owner-info": {
    ".avatar": {
      borderRadius: "50%",
      objectFit: "cover",
    },
    ".left-box": {
      maxWidth: "calc(100% - 48px)",
    },
  },

  ".company-avatar": {
    width: 48,
    height: 48,
    borderRadius: theme.shape.borderRadius,
    objectFit: "cover",
    flexShrink: 0,
  },

  [theme.breakpoints.down("md")]: {
    ".owner-info": {
      ".left-box, .buttons-box": {
        width: "100%",
      },
      ".buttons-box button": {
        padding: "4px 8px",
        lineHeight: "18px",
        svg: {
          width: 15,
          height: 15,
        },
      },
    },
  },

  ".category-link": {
    textDecoration: "none",
    marginLeft: 0,
    marginRight: 4,
    "&:hover": {
      textDecoration: "underline",
    },
  },

  ".view-product-btn": {
    display: "inline-block",
    fontWeight: 400,
  },

  ".farm-name-link": {
    display: "block",
    maxWidth: "100%",
    overflow: "hidden",
  },

  ".farm-name": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
  },

  ".product-name-link": {
    textDecoration: "none",
    position: "relative",
    zIndex: 1,
    display: "block",
    width: "100%",
    "&:hover": {
      textDecoration: "none",
    },
    "&:hover .product-name": {
      textDecoration: "underline",
    },
  },

  ".product-name": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
    display: "block",
    cursor: "pointer",
  },
}));

export default ListingItemCard;
